import { ChakraProvider, localStorageManager } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getHyperglassConfig, makeTheme } from '~/util';

import type { Config } from '~/types';

interface HyperglassProviderProps {
  config: Config;
  children: React.ReactNode;
}

export const HyperglassContext = createContext<Config>({} as Config);

export const queryClient = new QueryClient();

export const HyperglassProvider = (props: HyperglassProviderProps): JSX.Element => {
  const { config, children } = props;
  const [runtimeConfig, setRuntimeConfig] = useState<Config>({ ...config, devices: [] });

  useEffect(() => {
    let active = true;
    getHyperglassConfig()
      .then(nextConfig => {
        if (active) {
          setRuntimeConfig(nextConfig);
        }
      })
      .catch(() => {
        if (active) {
          setRuntimeConfig(config);
        }
      });
    return () => {
      active = false;
    };
  }, [config]);

  const value = useMemo(() => runtimeConfig, [runtimeConfig]);
  const theme = useMemo(
    () => makeTheme(value.web.theme, value.web.theme.defaultColorMode),
    [value.web.theme, value.web.theme.defaultColorMode],
  );

  return (
    <HyperglassContext.Provider value={value}>
      <ChakraProvider theme={theme} colorModeManager={localStorageManager} resetCSS>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </ChakraProvider>
    </HyperglassContext.Provider>
  );
};

/**
 * Get the current configuration.
 */
export const useConfig = (): Config => useContext(HyperglassContext);

